import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Buildings, ArrowLeft, ArrowRight, Check, Info, Certificate,
  ShieldCheck, Database, Robot, Users, Plus, X, Trash,
  Upload, FileText, Globe, Calendar, Warning, CheckCircle,
  Bank, User, Envelope, IdentificationCard, CaretDown, CaretUp
} from '@phosphor-icons/react';
import { API, useAuth } from '@/App';

// Organization capacity options
const ORGANIZATION_CAPACITIES = [
  { id: 'bank', label: 'Bank / Mutual Bank', description: 'Licensed banking institution under the Banks Act' },
  { id: 'insurer_long', label: 'Insurer (Long-term)', description: 'Life insurance, pension-backed policies' },
  { id: 'insurer_short', label: 'Insurer (Short-term)', description: 'Non-life insurance (motor, property, etc.)' },
  { id: 'investment_manager', label: 'Investment Manager / Discretionary FSP', description: 'Manages investments on behalf of clients' },
  { id: 'financial_advisor', label: 'Financial Advisor / Intermediary', description: 'Provides financial advice or intermediary services' },
  { id: 'cis_manager', label: 'Collective Investment Scheme (CIS) Manager', description: 'Manages unit trusts or collective investments' },
  { id: 'pension_fund', label: 'Retirement / Pension Fund', description: 'Administers retirement or pension funds' },
  { id: 'casp_vasp', label: 'Crypto Asset Service Provider (CASP/VASP)', description: 'Crypto exchanges, wallets, or virtual asset services' },
  { id: 'mobile_payment', label: 'Mobile Payment Provider', description: 'Mobile money or payment services' }
];

// FSP Categories
const FSP_CATEGORIES = [
  { id: 'cat_i', label: 'Category I', description: 'Advice and intermediary services (excluding discretionary)' },
  { id: 'cat_ii', label: 'Category II', description: 'Discretionary FSP' },
  { id: 'cat_iia', label: 'Category IIA', description: 'Hedge Fund FSP' },
  { id: 'cat_iii', label: 'Category III', description: 'Administrative FSP' },
  { id: 'cat_iv', label: 'Category IV', description: 'Assistance Business FSP' }
];

// Key Person roles
const KEY_PERSON_ROLES = [
  'Director',
  'Non-Executive Director',
  'Key Individual',
  'Chief Executive Officer',
  'Chief Financial Officer',
  'Chief Operating Officer',
  'Chief Risk Officer',
  'Board Chairperson',
  'Company Secretary'
];

// Tooltips
const ACCOUNTABLE_INSTITUTION_TOOLTIP = `Schedule 1 of the FIC Act lists "Accountable Institutions" including:
• Banks and Mutual Banks
• Life Insurers
• Financial Services Providers (FSPs)
• Attorneys and Trust Companies
• Estate Agents
• Crypto Asset Service Providers (CASPs)
• Motor Vehicle Dealers (high-value)
• Dealers in Precious Metals/Stones`;

// Step indicator component
const StepIndicator = ({ steps, currentStep }) => (
  <div className="flex items-center justify-center gap-2 mb-8">
    {steps.map((step, idx) => (
      <div key={idx} className="flex items-center">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
          idx === currentStep 
            ? 'bg-gold text-gold-foreground' 
            : idx < currentStep 
              ? 'bg-emerald-500 text-white'
              : 'bg-background-surface border border-border text-text-tertiary'
        }`}>
          {idx < currentStep ? <Check size={16} weight="bold" /> : idx + 1}
        </div>
        {idx < steps.length - 1 && (
          <div className={`w-8 h-0.5 mx-1 ${idx < currentStep ? 'bg-emerald-500' : 'bg-border'}`} />
        )}
      </div>
    ))}
  </div>
);

// Officer input component (for FAIS, MLRO, DPO)
const OfficerInputSection = ({ title, officers, setOfficers, maxOfficers = 5 }) => {
  const addOfficer = () => {
    if (officers.length < maxOfficers) {
      setOfficers([...officers, { name: '', surname: '', email: '' }]);
    }
  };

  const removeOfficer = (index) => {
    setOfficers(officers.filter((_, i) => i !== index));
  };

  const updateOfficer = (index, field, value) => {
    const updated = [...officers];
    updated[index] = { ...updated[index], [field]: value };
    setOfficers(updated);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-text-secondary">{title}</label>
        {officers.length < maxOfficers && (
          <button
            type="button"
            onClick={addOfficer}
            className="flex items-center gap-1 text-sm text-gold hover:underline"
          >
            <Plus size={14} />
            Add Officer
          </button>
        )}
      </div>
      
      {officers.length === 0 ? (
        <button
          type="button"
          onClick={addOfficer}
          className="w-full p-4 border border-dashed border-border rounded-lg text-text-tertiary hover:border-gold hover:text-gold transition-colors text-sm"
        >
          <Plus size={16} className="inline mr-2" />
          Add {title.split('(')[0].trim()}
        </button>
      ) : (
        <div className="space-y-2">
          {officers.map((officer, idx) => (
            <div key={idx} className="flex gap-2 items-start p-3 bg-background rounded-lg border border-border">
              <div className="flex-1 grid grid-cols-3 gap-2">
                <input
                  type="text"
                  value={officer.name}
                  onChange={(e) => updateOfficer(idx, 'name', e.target.value)}
                  placeholder="First Name"
                  className="px-3 py-2 bg-background-surface border border-border rounded-lg text-text-primary text-sm focus:border-gold focus:outline-none"
                />
                <input
                  type="text"
                  value={officer.surname}
                  onChange={(e) => updateOfficer(idx, 'surname', e.target.value)}
                  placeholder="Surname"
                  className="px-3 py-2 bg-background-surface border border-border rounded-lg text-text-primary text-sm focus:border-gold focus:outline-none"
                />
                <input
                  type="email"
                  value={officer.email}
                  onChange={(e) => updateOfficer(idx, 'email', e.target.value)}
                  placeholder="Email Address"
                  className="px-3 py-2 bg-background-surface border border-border rounded-lg text-text-primary text-sm focus:border-gold focus:outline-none"
                />
              </div>
              <button
                type="button"
                onClick={() => removeOfficer(idx)}
                className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg"
              >
                <Trash size={16} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Key Person input component
const KeyPersonInputSection = ({ keyPersons, setKeyPersons, maxPersons = 10 }) => {
  const addPerson = () => {
    if (keyPersons.length < maxPersons) {
      setKeyPersons([...keyPersons, { name: '', surname: '', email: '', role: '' }]);
    }
  };

  const removePerson = (index) => {
    setKeyPersons(keyPersons.filter((_, i) => i !== index));
  };

  const updatePerson = (index, field, value) => {
    const updated = [...keyPersons];
    updated[index] = { ...updated[index], [field]: value };
    setKeyPersons(updated);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-text-secondary">Key Persons</label>
        {keyPersons.length < maxPersons && (
          <button
            type="button"
            onClick={addPerson}
            className="flex items-center gap-1 text-sm text-gold hover:underline"
          >
            <Plus size={14} />
            Add Person
          </button>
        )}
      </div>
      
      {keyPersons.length === 0 ? (
        <button
          type="button"
          onClick={addPerson}
          className="w-full p-4 border border-dashed border-border rounded-lg text-text-tertiary hover:border-gold hover:text-gold transition-colors text-sm"
        >
          <Plus size={16} className="inline mr-2" />
          Add Key Person (Directors, Key Individuals, etc.)
        </button>
      ) : (
        <div className="space-y-2">
          {keyPersons.map((person, idx) => (
            <div key={idx} className="flex gap-2 items-start p-3 bg-background rounded-lg border border-border">
              <div className="flex-1 grid grid-cols-4 gap-2">
                <input
                  type="text"
                  value={person.name}
                  onChange={(e) => updatePerson(idx, 'name', e.target.value)}
                  placeholder="First Name"
                  className="px-3 py-2 bg-background-surface border border-border rounded-lg text-text-primary text-sm focus:border-gold focus:outline-none"
                />
                <input
                  type="text"
                  value={person.surname}
                  onChange={(e) => updatePerson(idx, 'surname', e.target.value)}
                  placeholder="Surname"
                  className="px-3 py-2 bg-background-surface border border-border rounded-lg text-text-primary text-sm focus:border-gold focus:outline-none"
                />
                <input
                  type="email"
                  value={person.email}
                  onChange={(e) => updatePerson(idx, 'email', e.target.value)}
                  placeholder="Email Address"
                  className="px-3 py-2 bg-background-surface border border-border rounded-lg text-text-primary text-sm focus:border-gold focus:outline-none"
                />
                <select
                  value={person.role}
                  onChange={(e) => updatePerson(idx, 'role', e.target.value)}
                  className="px-3 py-2 bg-background-surface border border-border rounded-lg text-text-primary text-sm focus:border-gold focus:outline-none"
                >
                  <option value="">Select Role</option>
                  {KEY_PERSON_ROLES.map(role => (
                    <option key={role} value={role}>{role}</option>
                  ))}
                </select>
              </div>
              <button
                type="button"
                onClick={() => removePerson(idx)}
                className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg"
              >
                <Trash size={16} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// License upload component
const LicenseUploadSection = ({ licenses, setLicenses }) => {
  const [showForm, setShowForm] = useState(false);
  const [newLicense, setNewLicense] = useState({
    license_type: '',
    license_number: '',
    issuing_authority: '',
    issue_date: '',
    expiry_date: '',
    file: null
  });

  const addLicense = () => {
    if (newLicense.license_number && newLicense.issuing_authority) {
      setLicenses([...licenses, { ...newLicense, id: Date.now() }]);
      setNewLicense({
        license_type: '',
        license_number: '',
        issuing_authority: '',
        issue_date: '',
        expiry_date: '',
        file: null
      });
      setShowForm(false);
    } else {
      toast.error('Please fill in license number and issuing authority');
    }
  };

  const removeLicense = (id) => {
    setLicenses(licenses.filter(l => l.id !== id));
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-text-secondary">Licenses & Registrations</label>
        {!showForm && (
          <button
            type="button"
            onClick={() => setShowForm(true)}
            className="flex items-center gap-1 text-sm text-gold hover:underline"
          >
            <Plus size={14} />
            Add License
          </button>
        )}
      </div>
      
      {licenses.length > 0 && (
        <div className="space-y-2">
          {licenses.map((license) => (
            <div key={license.id} className="flex items-center justify-between p-3 bg-background rounded-lg border border-border">
              <div>
                <div className="font-medium text-text-primary text-sm">{license.license_type || 'License'}</div>
                <div className="text-xs text-text-tertiary">
                  {license.license_number} • {license.issuing_authority}
                </div>
              </div>
              <button
                type="button"
                onClick={() => removeLicense(license.id)}
                className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg"
              >
                <Trash size={16} />
              </button>
            </div>
          ))}
        </div>
      )}
      
      {showForm && (
        <div className="p-4 bg-background rounded-lg border border-gold/30 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <input
              type="text"
              value={newLicense.license_type}
              onChange={(e) => setNewLicense({ ...newLicense, license_type: e.target.value })}
              placeholder="License Type (e.g., FSP License)"
              className="px-3 py-2 bg-background-surface border border-border rounded-lg text-text-primary text-sm focus:border-gold focus:outline-none"
            />
            <input
              type="text"
              value={newLicense.license_number}
              onChange={(e) => setNewLicense({ ...newLicense, license_number: e.target.value })}
              placeholder="License Number *"
              className="px-3 py-2 bg-background-surface border border-border rounded-lg text-text-primary text-sm focus:border-gold focus:outline-none"
            />
            <input
              type="text"
              value={newLicense.issuing_authority}
              onChange={(e) => setNewLicense({ ...newLicense, issuing_authority: e.target.value })}
              placeholder="Issuing Authority *"
              className="px-3 py-2 bg-background-surface border border-border rounded-lg text-text-primary text-sm focus:border-gold focus:outline-none"
            />
            <input
              type="date"
              value={newLicense.expiry_date}
              onChange={(e) => setNewLicense({ ...newLicense, expiry_date: e.target.value })}
              className="px-3 py-2 bg-background-surface border border-border rounded-lg text-text-primary text-sm focus:border-gold focus:outline-none"
              placeholder="Expiry Date"
            />
          </div>
          <div className="flex gap-2 justify-end">
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="px-3 py-2 border border-border rounded-lg text-text-secondary text-sm hover:bg-background-surface"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={addLicense}
              className="px-3 py-2 bg-gold text-gold-foreground rounded-lg text-sm font-medium hover:bg-gold/90"
            >
              Add License
            </button>
          </div>
        </div>
      )}
      
      {!showForm && licenses.length === 0 && (
        <button
          type="button"
          onClick={() => setShowForm(true)}
          className="w-full p-4 border border-dashed border-border rounded-lg text-text-tertiary hover:border-gold hover:text-gold transition-colors text-sm"
        >
          <Upload size={16} className="inline mr-2" />
          Add licenses and registrations
        </button>
      )}
    </div>
  );
};

// Yes/No Question component
const YesNoQuestion = ({ question, value, onChange, tooltip, children }) => (
  <div className="p-4 bg-background-surface rounded-lg border border-border">
    <div className="flex items-start justify-between gap-4">
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <span className="text-text-primary">{question}</span>
          {tooltip && (
            <div className="relative group">
              <Info size={16} className="text-text-tertiary cursor-help" />
              <div className="absolute bottom-full left-0 mb-2 w-72 p-3 bg-background border border-border rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                <p className="text-xs text-text-secondary whitespace-pre-line">{tooltip}</p>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => onChange(true)}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            value === true
              ? 'bg-emerald-500 text-white'
              : 'bg-background border border-border text-text-secondary hover:border-emerald-500'
          }`}
        >
          Yes
        </button>
        <button
          type="button"
          onClick={() => onChange(false)}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            value === false
              ? 'bg-red-500 text-white'
              : 'bg-background border border-border text-text-secondary hover:border-red-500'
          }`}
        >
          No
        </button>
      </div>
    </div>
    {children}
  </div>
);

// Main Component
const CompanyOverviewQuestionnaire = () => {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    // Step 1: Company Registration
    registered_company_name: '',
    registration_number: '',
    
    // Step 2: Compliance Officers
    fsp_number: '',
    fais_compliance_officers: [],
    fic_org_id: '',
    mlros: [],
    data_protection_officers: [],
    
    // Step 3: Business Profile
    primary_industry: 'Financial',
    organization_capacities: [],
    
    // Step 4: Licensing & Regulatory (from Intake)
    is_fsp: null,
    fsp_categories: [],
    takes_deposits: null,
    manages_client_assets: null,
    
    // Step 5: Financial Crime & AML (from Intake)
    is_accountable_institution: null,
    facilitates_fund_transfers: null,
    is_designated_fi_fatca_crs: null,
    
    // Step 6: Data & Privacy (from Intake)
    is_responsible_party: null,
    transfers_data_outside_sa: null,
    
    // Step 7: Technology & AI (from Intake)
    utilizes_ai: null,
    develops_ai: null,
    
    // Step 8: Operations
    countries_of_operation: '',
    compliance_cycle: 'calendar', // calendar or financial
    financial_year_end: '',
    
    // Step 9: Existing Universe
    has_existing_universe: null,
    continue_existing_universe: null,
    
    // Step 10: Key Persons & Licenses
    key_persons: [],
    licenses: []
  });

  const steps = [
    { title: 'Company Registration', icon: <Buildings size={20} /> },
    { title: 'Compliance Officers', icon: <Users size={20} /> },
    { title: 'Business Profile', icon: <Bank size={20} /> },
    { title: 'Licensing & Regulatory', icon: <Certificate size={20} /> },
    { title: 'Financial Crime & AML', icon: <ShieldCheck size={20} /> },
    { title: 'Data & Privacy', icon: <Database size={20} /> },
    { title: 'Technology & AI', icon: <Robot size={20} /> },
    { title: 'Operations', icon: <Globe size={20} /> },
    { title: 'Regulatory Universe', icon: <FileText size={20} /> },
    { title: 'Key Persons & Licenses', icon: <IdentificationCard size={20} /> }
  ];

  const canProceed = () => {
    switch (currentStep) {
      case 0:
        return formData.registered_company_name.trim() !== '';
      case 1:
        return true; // Officers are optional
      case 2:
        return formData.organization_capacities.length > 0;
      case 3:
        return formData.is_fsp !== null;
      case 4:
        return formData.is_accountable_institution !== null;
      case 5:
        return formData.is_responsible_party !== null;
      case 6:
        return true; // AI is optional
      case 7:
        return formData.compliance_cycle !== '';
      case 8:
        return formData.has_existing_universe !== null;
      case 9:
        return true; // Key persons and licenses optional
      default:
        return true;
    }
  };

  const handleCapacityToggle = (capacityId) => {
    setFormData(prev => ({
      ...prev,
      organization_capacities: prev.organization_capacities.includes(capacityId)
        ? prev.organization_capacities.filter(c => c !== capacityId)
        : [...prev.organization_capacities, capacityId]
    }));
  };

  const handleFspCategoryToggle = (categoryId) => {
    setFormData(prev => ({
      ...prev,
      fsp_categories: prev.fsp_categories.includes(categoryId)
        ? prev.fsp_categories.filter(c => c !== categoryId)
        : [...prev.fsp_categories, categoryId]
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Submit company overview questionnaire
      const response = await axios.post(`${API}/company-overview/questionnaire`, formData);
      
      toast.success('Company overview saved successfully!');
      
      // Refresh user data to update questionnaire_completed flag
      if (refreshUser) {
        await refreshUser();
      }
      
      // Navigate based on user's choice about existing universe
      if (formData.has_existing_universe && formData.continue_existing_universe) {
        // Redirect to upload existing universe page
        navigate('/universe/upload', { state: { fromQuestionnaire: true, formData } });
      } else {
        // Generate new universe from questionnaire data - pass intake ID for universe generation
        const intakeId = response.data.intake_id;
        navigate('/navi-intro', { state: { intakeId, fromQuestionnaire: true } });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save company overview');
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = async () => {
    try {
      // Mark questionnaire as skipped on the backend
      await axios.post(`${API}/company-overview/skip`);
      
      // Refresh user data
      if (refreshUser) {
        await refreshUser();
      }
      
      toast.info('You can complete the questionnaire anytime from Company Overview');
      navigate('/dashboard');
    } catch (error) {
      // If endpoint fails, just navigate anyway (user can still access)
      console.error('Skip error:', error);
      navigate('/dashboard');
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-heading font-semibold text-text-primary mb-2">Company Registration Details</h2>
              <p className="text-text-tertiary">Enter your company's official registration information</p>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">
                  Registered Company Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={formData.registered_company_name}
                  onChange={(e) => setFormData({ ...formData, registered_company_name: e.target.value })}
                  placeholder="Enter your registered company name"
                  className="w-full px-4 py-3 bg-background-surface border border-border rounded-lg text-text-primary focus:border-gold focus:outline-none"
                  data-testid="company-name-input"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">
                  Registration Number
                </label>
                <input
                  type="text"
                  value={formData.registration_number}
                  onChange={(e) => setFormData({ ...formData, registration_number: e.target.value })}
                  placeholder="e.g., 2020/123456/07"
                  className="w-full px-4 py-3 bg-background-surface border border-border rounded-lg text-text-primary focus:border-gold focus:outline-none"
                />
              </div>
            </div>
          </div>
        );

      case 1:
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-heading font-semibold text-text-primary mb-2">Compliance Officers</h2>
              <p className="text-text-tertiary">Register your key compliance personnel (up to 5 per category)</p>
            </div>
            
            <div className="space-y-6">
              {/* FSP and FAIS Compliance Officers */}
              <div className="p-4 bg-background-surface rounded-lg border border-border">
                <div className="mb-4">
                  <label className="block text-sm font-medium text-text-secondary mb-2">FSP Number</label>
                  <input
                    type="text"
                    value={formData.fsp_number}
                    onChange={(e) => setFormData({ ...formData, fsp_number: e.target.value })}
                    placeholder="Enter your FSP number"
                    className="w-full px-4 py-3 bg-background border border-border rounded-lg text-text-primary focus:border-gold focus:outline-none"
                  />
                </div>
                <OfficerInputSection
                  title="Licensed FAIS Compliance Officer(s)"
                  officers={formData.fais_compliance_officers}
                  setOfficers={(officers) => setFormData({ ...formData, fais_compliance_officers: officers })}
                />
              </div>
              
              {/* FIC and MLROs */}
              <div className="p-4 bg-background-surface rounded-lg border border-border">
                <div className="mb-4">
                  <label className="block text-sm font-medium text-text-secondary mb-2">FIC Org ID</label>
                  <input
                    type="text"
                    value={formData.fic_org_id}
                    onChange={(e) => setFormData({ ...formData, fic_org_id: e.target.value })}
                    placeholder="Enter your FIC Organisation ID"
                    className="w-full px-4 py-3 bg-background border border-border rounded-lg text-text-primary focus:border-gold focus:outline-none"
                  />
                </div>
                <OfficerInputSection
                  title="Money Laundering Reporting Officer(s) (MLRO)"
                  officers={formData.mlros}
                  setOfficers={(officers) => setFormData({ ...formData, mlros: officers })}
                />
              </div>
              
              {/* Data Protection Officers */}
              <div className="p-4 bg-background-surface rounded-lg border border-border">
                <OfficerInputSection
                  title="Data Protection Officer(s) (DPO)"
                  officers={formData.data_protection_officers}
                  setOfficers={(officers) => setFormData({ ...formData, data_protection_officers: officers })}
                />
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-heading font-semibold text-text-primary mb-2">Business Profile</h2>
              <p className="text-text-tertiary">Select all capacities in which your organisation operates</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-2">Primary Industry</label>
              <input
                type="text"
                value={formData.primary_industry}
                onChange={(e) => setFormData({ ...formData, primary_industry: e.target.value })}
                className="w-full px-4 py-3 bg-background-surface border border-border rounded-lg text-text-primary focus:border-gold focus:outline-none"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-3">
                Operating Capacities <span className="text-red-400">*</span>
              </label>
              <div className="grid md:grid-cols-2 gap-3">
                {ORGANIZATION_CAPACITIES.map((capacity) => (
                  <button
                    key={capacity.id}
                    type="button"
                    onClick={() => handleCapacityToggle(capacity.id)}
                    className={`p-4 rounded-lg border text-left transition-all ${
                      formData.organization_capacities.includes(capacity.id)
                        ? 'bg-gold/10 border-gold text-text-primary'
                        : 'bg-background-surface border-border text-text-secondary hover:border-gold/50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded border flex items-center justify-center ${
                        formData.organization_capacities.includes(capacity.id)
                          ? 'bg-gold border-gold'
                          : 'border-border'
                      }`}>
                        {formData.organization_capacities.includes(capacity.id) && (
                          <Check size={12} className="text-gold-foreground" weight="bold" />
                        )}
                      </div>
                      <div>
                        <div className="font-medium text-sm">{capacity.label}</div>
                        <div className="text-xs text-text-tertiary">{capacity.description}</div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-heading font-semibold text-text-primary mb-2">Licensing & Regulatory Status</h2>
              <p className="text-text-tertiary">Tell us about your regulatory licenses and authorizations</p>
            </div>
            
            <div className="space-y-4">
              <YesNoQuestion
                question="Is your organisation a licensed Financial Services Provider (FSP)?"
                value={formData.is_fsp}
                onChange={(val) => setFormData({ ...formData, is_fsp: val })}
              >
                {formData.is_fsp && (
                  <div className="mt-4 pt-4 border-t border-border">
                    <label className="block text-sm text-text-secondary mb-2">Select applicable FSP Categories:</label>
                    <div className="flex flex-wrap gap-2">
                      {FSP_CATEGORIES.map((cat) => (
                        <button
                          key={cat.id}
                          type="button"
                          onClick={() => handleFspCategoryToggle(cat.id)}
                          className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                            formData.fsp_categories.includes(cat.id)
                              ? 'bg-gold text-gold-foreground'
                              : 'bg-background border border-border text-text-secondary hover:border-gold'
                          }`}
                        >
                          {cat.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </YesNoQuestion>
              
              <YesNoQuestion
                question="Does your organisation take deposits from the public?"
                value={formData.takes_deposits}
                onChange={(val) => setFormData({ ...formData, takes_deposits: val })}
              />
              
              <YesNoQuestion
                question="Does your organisation manage or safeguard client assets?"
                value={formData.manages_client_assets}
                onChange={(val) => setFormData({ ...formData, manages_client_assets: val })}
              />
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-heading font-semibold text-text-primary mb-2">Financial Crime & AML</h2>
              <p className="text-text-tertiary">FICA, FATF, and anti-money laundering obligations</p>
            </div>
            
            <div className="space-y-4">
              <YesNoQuestion
                question="Is your organisation an Accountable Institution under Schedule 1 of the FIC Act?"
                value={formData.is_accountable_institution}
                onChange={(val) => setFormData({ ...formData, is_accountable_institution: val })}
                tooltip={ACCOUNTABLE_INSTITUTION_TOOLTIP}
              />
              
              <YesNoQuestion
                question="Does your organisation facilitate fund transfers (e.g., payments, remittances)?"
                value={formData.facilitates_fund_transfers}
                onChange={(val) => setFormData({ ...formData, facilitates_fund_transfers: val })}
              />
              
              <YesNoQuestion
                question="Is your organisation a Designated Financial Institution for FATCA/CRS purposes?"
                value={formData.is_designated_fi_fatca_crs}
                onChange={(val) => setFormData({ ...formData, is_designated_fi_fatca_crs: val })}
              />
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-heading font-semibold text-text-primary mb-2">Data & Privacy</h2>
              <p className="text-text-tertiary">POPIA and data protection compliance</p>
            </div>
            
            <div className="space-y-4">
              <YesNoQuestion
                question="Is your organisation a Responsible Party (Data Controller) under POPIA?"
                value={formData.is_responsible_party}
                onChange={(val) => setFormData({ ...formData, is_responsible_party: val })}
              />
              
              <YesNoQuestion
                question="Does your organisation transfer personal information outside South Africa?"
                value={formData.transfers_data_outside_sa}
                onChange={(val) => setFormData({ ...formData, transfers_data_outside_sa: val })}
              />
            </div>
          </div>
        );

      case 6:
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-heading font-semibold text-text-primary mb-2">Technology & AI</h2>
              <p className="text-text-tertiary">AI governance and technology-related compliance</p>
            </div>
            
            <div className="space-y-4">
              <YesNoQuestion
                question="Does your organisation utilise AI systems in its operations?"
                value={formData.utilizes_ai}
                onChange={(val) => setFormData({ ...formData, utilizes_ai: val })}
              />
              
              <YesNoQuestion
                question="Does your organisation develop or deploy AI systems for clients?"
                value={formData.develops_ai}
                onChange={(val) => setFormData({ ...formData, develops_ai: val })}
              />
            </div>
          </div>
        );

      case 7:
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-heading font-semibold text-text-primary mb-2">Operations</h2>
              <p className="text-text-tertiary">Geographic and operational details</p>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">
                  Countries of Operation
                </label>
                <textarea
                  value={formData.countries_of_operation}
                  onChange={(e) => setFormData({ ...formData, countries_of_operation: e.target.value })}
                  placeholder="List the countries where your organisation operates (e.g., South Africa, Namibia, Botswana)"
                  rows={3}
                  className="w-full px-4 py-3 bg-background-surface border border-border rounded-lg text-text-primary focus:border-gold focus:outline-none resize-none"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-3">
                  Compliance Cycle <span className="text-red-400">*</span>
                </label>
                <div className="flex gap-4">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, compliance_cycle: 'calendar', financial_year_end: '' })}
                    className={`flex-1 p-4 rounded-lg border text-center transition-all ${
                      formData.compliance_cycle === 'calendar'
                        ? 'bg-gold/10 border-gold text-text-primary'
                        : 'bg-background-surface border-border text-text-secondary hover:border-gold/50'
                    }`}
                  >
                    <Calendar size={24} className="mx-auto mb-2" />
                    <div className="font-medium">Calendar Year</div>
                    <div className="text-xs text-text-tertiary">Jan - Dec</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, compliance_cycle: 'financial' })}
                    className={`flex-1 p-4 rounded-lg border text-center transition-all ${
                      formData.compliance_cycle === 'financial'
                        ? 'bg-gold/10 border-gold text-text-primary'
                        : 'bg-background-surface border-border text-text-secondary hover:border-gold/50'
                    }`}
                  >
                    <Bank size={24} className="mx-auto mb-2" />
                    <div className="font-medium">Financial Year</div>
                    <div className="text-xs text-text-tertiary">Custom period</div>
                  </button>
                </div>
                
                {formData.compliance_cycle === 'financial' && (
                  <div className="mt-4">
                    <label className="block text-sm text-text-secondary mb-2">Financial Year End</label>
                    <input
                      type="date"
                      value={formData.financial_year_end}
                      onChange={(e) => setFormData({ ...formData, financial_year_end: e.target.value })}
                      className="px-4 py-3 bg-background-surface border border-border rounded-lg text-text-primary focus:border-gold focus:outline-none"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case 8:
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-heading font-semibold text-text-primary mb-2">Regulatory Universe</h2>
              <p className="text-text-tertiary">Do you have an existing regulatory universe?</p>
            </div>
            
            <div className="space-y-4">
              <YesNoQuestion
                question="Do you currently have an established Regulatory Universe with a defined scoring methodology?"
                value={formData.has_existing_universe}
                onChange={(val) => setFormData({ ...formData, has_existing_universe: val, continue_existing_universe: null })}
              />
              
              {formData.has_existing_universe && (
                <div className="p-4 bg-background-surface rounded-lg border border-gold/30">
                  <label className="block text-sm font-medium text-text-secondary mb-3">
                    Would you like to continue using your existing Regulatory Universe, or create a new one?
                  </label>
                  <div className="flex gap-4">
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, continue_existing_universe: true })}
                      className={`flex-1 p-4 rounded-lg border text-center transition-all ${
                        formData.continue_existing_universe === true
                          ? 'bg-gold/10 border-gold text-text-primary'
                          : 'bg-background border-border text-text-secondary hover:border-gold/50'
                      }`}
                    >
                      <Upload size={24} className="mx-auto mb-2" />
                      <div className="font-medium">Upload Existing</div>
                      <div className="text-xs text-text-tertiary">Use my current universe</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, continue_existing_universe: false })}
                      className={`flex-1 p-4 rounded-lg border text-center transition-all ${
                        formData.continue_existing_universe === false
                          ? 'bg-gold/10 border-gold text-text-primary'
                          : 'bg-background border-border text-text-secondary hover:border-gold/50'
                      }`}
                    >
                      <FileText size={24} className="mx-auto mb-2" />
                      <div className="font-medium">Create New</div>
                      <div className="text-xs text-text-tertiary">Generate from questionnaire</div>
                    </button>
                  </div>
                </div>
              )}
              
              {!formData.has_existing_universe && formData.has_existing_universe !== null && (
                <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                  <div className="flex items-start gap-3">
                    <CheckCircle size={20} className="text-emerald-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="font-medium text-emerald-400">We'll create your Regulatory Universe</div>
                      <div className="text-sm text-text-tertiary mt-1">
                        Based on your questionnaire responses, we'll automatically generate a tailored regulatory universe for your organisation.
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      case 9:
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-heading font-semibold text-text-primary mb-2">Key Persons & Licenses</h2>
              <p className="text-text-tertiary">Add your key personnel and business licenses</p>
            </div>
            
            <KeyPersonInputSection
              keyPersons={formData.key_persons}
              setKeyPersons={(persons) => setFormData({ ...formData, key_persons: persons })}
            />
            
            <div className="pt-4 border-t border-border">
              <LicenseUploadSection
                licenses={formData.licenses}
                setLicenses={(licenses) => setFormData({ ...formData, licenses: licenses })}
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-3xl">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-heading font-bold text-gold mb-2">Welcome to Navigate Aura</h1>
          <p className="text-text-tertiary">Complete your company profile to get started</p>
        </div>
        
        {/* Step Indicator */}
        <StepIndicator steps={steps} currentStep={currentStep} />
        
        {/* Current Step Title */}
        <div className="flex items-center gap-3 mb-6 justify-center">
          <div className="w-10 h-10 rounded-lg bg-gold/20 flex items-center justify-center text-gold">
            {steps[currentStep].icon}
          </div>
          <span className="font-heading font-semibold text-text-primary">
            {steps[currentStep].title}
          </span>
        </div>
        
        {/* Form Card */}
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          className="bg-background-surface border border-border rounded-lg p-8"
        >
          {renderStep()}
        </motion.div>
        
        {/* Navigation */}
        <div className="flex items-center justify-between mt-6">
          <button
            type="button"
            onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
            disabled={currentStep === 0}
            className="flex items-center gap-2 px-4 py-2 border border-border rounded-lg text-text-secondary hover:bg-background-surface disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowLeft size={18} />
            Back
          </button>
          
          <span className="text-sm text-text-tertiary">
            Step {currentStep + 1} of {steps.length}
          </span>
          
          {currentStep < steps.length - 1 ? (
            <button
              type="button"
              onClick={() => setCurrentStep(currentStep + 1)}
              disabled={!canProceed()}
              className="flex items-center gap-2 px-6 py-2 bg-gold text-gold-foreground rounded-lg font-medium hover:bg-gold/90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
              <ArrowRight size={18} />
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading || !canProceed()}
              className="flex items-center gap-2 px-6 py-2 bg-gold text-gold-foreground rounded-lg font-medium hover:bg-gold/90 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="submit-questionnaire-btn"
            >
              {loading ? 'Submitting...' : 'Complete Setup'}
              <Check size={18} />
            </button>
          )}
        </div>
        
        {/* Skip for now option */}
        <div className="text-center mt-4">
          <button
            type="button"
            onClick={handleSkip}
            className="text-sm text-text-tertiary hover:text-text-secondary transition-colors"
            data-testid="skip-questionnaire-btn"
          >
            Skip for now and explore the platform
          </button>
          <p className="text-xs text-text-tertiary mt-1">
            You can complete this later from Company Overview
          </p>
        </div>
      </div>
    </div>
  );
};

export default CompanyOverviewQuestionnaire;
